/**
 * Canonical client-side memory of the seeker's most recent kundali reading.
 *
 * Every generated kundali is persisted server-side under its reading id and
 * re-openable at /chat/<id> (consultation) and /kundali/<id> (chart). This
 * module is the ONE place that remembers which reading this browser minted
 * last, so chat entry surfaces can offer "continue your consultation" instead
 * of an empty birth form (only ~18% of chat-entry sessions submit details —
 * returning seekers shouldn't pay that friction twice).
 *
 * Storage shape: the legacy bare-id key is kept (FloatingFeedback reads it);
 * the meta key adds language + first name for the resume card. All access is
 * soft-failing — private mode / quota never breaks the page.
 */

const ID_KEY = "ish_last_reading_id";
const META_KEY = "ish_last_reading_meta";

export interface LastReading {
  id: string;
  language: "en" | "hi";
  /** First name from the birth details — display only, never re-submitted. */
  name?: string;
  savedAt?: string;
}

// Mini external store for React consumers (useSyncExternalStore): a cached
// snapshot keeps object identity stable across getSnapshot calls (required by
// React), and remember/clear notify subscribers so dismissal re-renders.
let snapshot: LastReading | null | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeLastReading(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Stable-identity snapshot for useSyncExternalStore (server snapshot: null). */
export function getLastReadingSnapshot(): LastReading | null {
  if (snapshot === undefined) snapshot = getLastReading();
  return snapshot;
}

export function rememberLastReading(reading: LastReading): void {
  try {
    if (typeof window === "undefined" || !reading.id) return;
    const stored = { ...reading, savedAt: new Date().toISOString() };
    window.localStorage.setItem(ID_KEY, reading.id);
    window.localStorage.setItem(META_KEY, JSON.stringify(stored));
    snapshot = stored;
    emit();
  } catch {
    /* private mode / quota — non-fatal */
  }
}

export function getLastReading(): LastReading | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LastReading;
      if (parsed && typeof parsed.id === "string" && parsed.id) {
        return { ...parsed, language: parsed.language === "hi" ? "hi" : "en" };
      }
    }
    // Pre-meta readings: the bare id is still resumable (language defaults en).
    const id = window.localStorage.getItem(ID_KEY);
    return id ? { id, language: "en" } : null;
  } catch {
    return null;
  }
}

/**
 * Forget the stored reading — called when the seeker explicitly starts fresh
 * or when /chat/<id> reports the reading no longer exists (so a dead link can
 * never keep resurfacing as a resume card).
 */
export function clearLastReading(onlyIfId?: string): void {
  try {
    if (typeof window === "undefined") return;
    if (onlyIfId) {
      const current = getLastReading();
      if (current && current.id !== onlyIfId) return;
    }
    window.localStorage.removeItem(ID_KEY);
    window.localStorage.removeItem(META_KEY);
    snapshot = null;
    emit();
  } catch {
    /* non-fatal */
  }
}
